import { useCallback, useEffect, useMemo, useState } from "react";
import type { ICategoriaProduto, ICupomLoja, IProdutoLoja } from "interfaces/loja";

/** Linha na lista de vínculos: produto sem SKU ou uma SKU específica. */
export type VinculoLinhaCupom =
  | { key: string; tipo: "produto"; produtoId: string }
  | {
      key: string;
      tipo: "variacao";
      produtoId: string;
      variacaoId: string;
      sku?: string;
    };

export function buildVinculosLinhasFromCupom(
  cupom: ICupomLoja
): VinculoLinhaCupom[] {
  const linhas: VinculoLinhaCupom[] = [];
  const cupVars = cupom.cupomVariacoes ?? [];
  const parentsFromVars = new Set(
    cupVars
      .map((cv) => cv.variacao?.produtoId)
      .filter((id): id is string => Boolean(id))
  );

  for (const cv of cupVars) {
    const pid = cv.variacao?.produtoId;
    if (!pid) continue;
    linhas.push({
      key: `v:${cv.variacaoId}`,
      tipo: "variacao",
      produtoId: pid,
      variacaoId: cv.variacaoId,
      sku: cv.variacao?.sku,
    });
  }

  for (const cp of cupom.cupomProdutos ?? []) {
    if (!parentsFromVars.has(cp.produtoId)) {
      linhas.push({
        key: `p:${cp.produtoId}`,
        tipo: "produto",
        produtoId: cp.produtoId,
      });
    }
  }

  return linhas;
}

function tituloLinhaVinculada(
  linha: VinculoLinhaCupom,
  produtosInfo: Record<string, { nome: string; sku?: string }>
): string {
  const info = produtosInfo[linha.produtoId];
  const nome = info?.nome;
  if (linha.tipo === "variacao") {
    const sku = linha.sku ?? info?.sku;
    if (nome && sku) return `${nome} · SKU ${sku}`;
    if (nome) return nome;
    if (sku) return `SKU ${sku}`;
    return `Produto ${linha.produtoId.slice(0, 8)}…`;
  }
  const sku = info?.sku;
  if (nome && sku) return `${nome} · SKU ${sku}`;
  if (nome) return nome;
  return `Produto ${linha.produtoId.slice(0, 8)}…`;
}

function linhaBuscaJaVinculada(
  row: { produtoId: string; variacaoId?: string },
  vinculos: VinculoLinhaCupom[]
): boolean {
  if (row.variacaoId) {
    return vinculos.some(
      (v) => v.tipo === "variacao" && v.variacaoId === row.variacaoId
    );
  }
  return vinculos.some(
    (v) => v.tipo === "produto" && v.produtoId === row.produtoId
  );
}
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import MDTypography from "components/MDTypography";
import Icon from "@mui/material/Icon";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import Collapse from "@mui/material/Collapse";
import Switch from "@mui/material/Switch";
import FormControl from "@mui/material/FormControl";
import FormLabel from "@mui/material/FormLabel";
import RadioGroup from "@mui/material/RadioGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import Radio from "@mui/material/Radio";
import CircularProgress from "@mui/material/CircularProgress";

export type OpcaoVinculo = "global" | "produtos" | "categorias";

export interface CupomFormVinculosProps {
  disabled: boolean;
  opcaoVinculo: OpcaoVinculo;
  onOpcaoVinculoChange: (v: OpcaoVinculo) => void;
  buscaProduto: string;
  onBuscaProdutoChange: (v: string) => void;
  onBuscarProdutos: (pagina?: number) => void;
  produtosBusca: IProdutoLoja[];
  vinculosLinhas: VinculoLinhaCupom[];
  produtosInfo: Record<string, { nome: string; sku?: string }>;
  onVincularBusca: (spec: {
    produtoId: string;
    variacaoId?: string;
  }) => void;
  onDesvincularLinha: (linha: VinculoLinhaCupom) => void;
  pageBusca: number;
  hasMaisBusca: boolean;
  vincLoading: boolean;
  categoriasArvore: ICategoriaProduto[];
  categoriasVinculadas: number[];
  onToggleCategoria: (id: number) => void;
}

function CategoriaTreeItem({
  categoria,
  level,
  expanded,
  onToggle,
  vinculadas,
  onToggleCategoria,
}: {
  categoria: ICategoriaProduto;
  level: number;
  expanded: Set<number>;
  onToggle: (id: number) => void;
  vinculadas: number[];
  onToggleCategoria: (id: number) => void;
}) {
  const hasChildren = Boolean(categoria.subcategorias?.length);
  const isExpanded = expanded.has(categoria.id);
  const checked = vinculadas.includes(categoria.id);

  return (
    <>
      <ListItem disablePadding sx={{ pl: level * 2, mb: 0.5 }}>
        <ListItemButton
          onClick={() => hasChildren && onToggle(categoria.id)}
          sx={{ borderRadius: 1, py: 1, px: 1.5 }}
        >
          {hasChildren ? (
            <Icon sx={{ mr: 1, fontSize: 20, minWidth: 24 }}>
              {isExpanded ? "expand_more" : "chevron_right"}
            </Icon>
          ) : (
            <MDBox width={24} mr={1} />
          )}
          <ListItemText
            primary={
              <MDBox display="flex" alignItems="center" gap={1}>
                <Switch
                  size="small"
                  checked={checked}
                  onChange={(e) => {
                    e.stopPropagation();
                    onToggleCategoria(categoria.id);
                  }}
                  onClick={(e) => e.stopPropagation()}
                />
                <MDTypography variant="body2">{categoria.nome}</MDTypography>
              </MDBox>
            }
          />
        </ListItemButton>
      </ListItem>
      {hasChildren && (
        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            {categoria.subcategorias!.map((sub) => (
              <CategoriaTreeItem
                key={sub.id}
                categoria={sub}
                level={level + 1}
                expanded={expanded}
                onToggle={onToggle}
                vinculadas={vinculadas}
                onToggleCategoria={onToggleCategoria}
              />
            ))}
          </List>
        </Collapse>
      )}
    </>
  );
}

export default function CupomFormVinculos({
  disabled,
  opcaoVinculo,
  onOpcaoVinculoChange,
  buscaProduto,
  onBuscaProdutoChange,
  onBuscarProdutos,
  produtosBusca,
  vinculosLinhas,
  produtosInfo,
  onVincularBusca,
  onDesvincularLinha,
  pageBusca,
  hasMaisBusca,
  vincLoading,
  categoriasArvore,
  categoriasVinculadas,
  onToggleCategoria,
}: CupomFormVinculosProps) {
  const [expanded, setExpanded] = useState<Set<number>>(new Set());

  useEffect(() => {
    if (categoriasArvore.length) {
      setExpanded(new Set(categoriasArvore.map((c) => c.id)));
    }
  }, [categoriasArvore]);

  const toggleExpanded = useCallback((id: number) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const linhasBusca = useMemo(() => {
    const out: Array<{
      chave: string;
      produtoId: string;
      variacaoId?: string;
      label: string;
    }> = [];
    for (const p of produtosBusca) {
      const vars = p.variacoes ?? [];
      if (vars.length === 0) {
        out.push({
          chave: `p:${p.id}`,
          produtoId: String(p.id),
          label: p.nome,
        });
      } else {
        for (const v of vars) {
          const sku = String((v as { sku?: string }).sku ?? "").trim();
          out.push({
            chave: `v:${(v as { id: string }).id}`,
            produtoId: String(p.id),
            variacaoId: String((v as { id: string }).id),
            label: sku ? `${p.nome} · SKU ${sku}` : p.nome,
          });
        }
      }
    }
    return out;
  }, [produtosBusca]);

  if (disabled) {
    return (
      <MDBox mt={2} p={3} sx={{ bgcolor: "action.hover", borderRadius: 2 }}>
        <MDTypography variant="body2" color="text">
          Crie o cupom na aba Dados antes de vincular produtos ou categorias.
        </MDTypography>
      </MDBox>
    );
  }

  return (
    <MDBox mt={2} display="flex" flexDirection="column" gap={2}>
      <FormControl component="fieldset">
        <FormLabel component="legend">Tipo de vínculo</FormLabel>
        <RadioGroup
          row
          value={opcaoVinculo}
          onChange={(_, v) => onOpcaoVinculoChange(v as OpcaoVinculo)}
        >
          <FormControlLabel
            value="global"
            disabled={
              !(
                vinculosLinhas.length === 0 &&
                categoriasVinculadas.length === 0
              )
            }
            control={<Radio />}
            label="Cupom (Global)"
          />
          <FormControlLabel
            value="produtos"
            disabled={categoriasVinculadas.length > 0}
            control={<Radio />}
            label="Produtos"
          />
          <FormControlLabel
            value="categorias"
            disabled={vinculosLinhas.length > 0}
            control={<Radio />}
            label="Categorias"
          />
        </RadioGroup>
      </FormControl>

      {opcaoVinculo === "produtos" && (
        <>
          <MDBox display="flex" gap={1} alignItems="center" flexWrap="wrap">
            <MDInput
              placeholder="Buscar produto (mín. 2 caracteres)"
              value={buscaProduto}
              onChange={(e: any) => onBuscaProdutoChange(e.target.value)}
              onKeyDown={(e: any) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  if (buscaProduto.trim().length >= 2) onBuscarProdutos(1);
                }
              }}
              sx={{ flexGrow: 1 }}
            />
            <MDButton
              variant="outlined"
              color="dark"
              onClick={() => onBuscarProdutos(1)}
              disabled={buscaProduto.trim().length < 2 || vincLoading}
            >
              <Icon>search</Icon>&nbsp;Pesquisar
            </MDButton>
            <MDBox display="flex" alignItems="center" gap={0.5}>
              <MDButton
                size="small"
                variant="outlined"
                onClick={() => onBuscarProdutos(Math.max(1, pageBusca - 1))}
                disabled={vincLoading || pageBusca <= 1}
              >
                <Icon>chevron_left</Icon>
              </MDButton>
              <MDTypography variant="caption">Página {pageBusca}</MDTypography>
              <MDButton
                size="small"
                variant="outlined"
                onClick={() => onBuscarProdutos(pageBusca + 1)}
                disabled={vincLoading || !hasMaisBusca}
              >
                <Icon>chevron_right</Icon>
              </MDButton>
            </MDBox>
          </MDBox>

          <MDBox>
            <MDTypography variant="button" fontWeight="medium" mb={1}>
              Vinculados
            </MDTypography>
            {vinculosLinhas.length === 0 ? (
              <MDTypography variant="caption" color="text">
                Nenhum produto vinculado.
              </MDTypography>
            ) : (
              <MDBox display="flex" flexDirection="column" gap={1}>
                {vinculosLinhas.map((linha) => (
                  <MDBox
                    key={linha.key}
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                    p={1}
                    sx={{
                      border: "1px solid",
                      borderColor: "divider",
                      borderRadius: 1,
                    }}
                  >
                    <MDTypography variant="button">
                      {tituloLinhaVinculada(linha, produtosInfo)}
                    </MDTypography>
                    <MDButton
                      size="small"
                      variant="outlined"
                      color="error"
                      onClick={() => onDesvincularLinha(linha)}
                    >
                      <Icon fontSize="small" color="error">
                        delete
                      </Icon>
                      &nbsp;Desvincular
                    </MDButton>
                  </MDBox>
                ))}
              </MDBox>
            )}
          </MDBox>

          <MDBox>
            <MDTypography variant="button" fontWeight="medium" mb={1}>
              Resultado da busca
            </MDTypography>
            {vincLoading ? (
              <MDBox display="flex" alignItems="center" gap={1} p={2}>
                <CircularProgress size={20} />
                <MDTypography variant="caption">Carregando...</MDTypography>
              </MDBox>
            ) : linhasBusca.length === 0 ? (
              <MDTypography variant="caption" color="text">
                {buscaProduto.trim().length < 2
                  ? "Digite ao menos 2 caracteres para buscar."
                  : "Nenhum resultado."}
              </MDTypography>
            ) : (
              <MDBox display="flex" flexDirection="column" gap={1}>
                {linhasBusca.map((row) => {
                  const ja = linhaBuscaJaVinculada(row, vinculosLinhas);
                  return (
                    <MDBox
                      key={row.chave}
                      display="flex"
                      alignItems="center"
                      justifyContent="space-between"
                      p={1}
                      sx={{
                        border: "1px solid",
                        borderColor: "divider",
                        borderRadius: 1,
                      }}
                    >
                      <MDTypography variant="button">{row.label}</MDTypography>
                      <MDButton
                        size="small"
                        variant="outlined"
                        color={ja ? "secondary" : "success"}
                        disabled={ja}
                        onClick={() =>
                          onVincularBusca({
                            produtoId: row.produtoId,
                            variacaoId: row.variacaoId,
                          })
                        }
                      >
                        <Icon fontSize="small">add</Icon>&nbsp;
                        {ja ? "Já vinculado" : "Vincular"}
                      </MDButton>
                    </MDBox>
                  );
                })}
              </MDBox>
            )}
          </MDBox>
        </>
      )}

      {opcaoVinculo === "categorias" && (
        <MDBox>
          <MDTypography variant="button" fontWeight="medium" mb={1}>
            Selecionar categorias (marque para vincular)
          </MDTypography>
          <MDBox
            sx={{
              maxHeight: 360,
              overflow: "auto",
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 1,
              p: 1,
            }}
          >
            {vincLoading ? (
              <MDBox display="flex" alignItems="center" gap={1} p={2}>
                <CircularProgress size={20} />
                <MDTypography variant="caption">Carregando...</MDTypography>
              </MDBox>
            ) : categoriasArvore.length === 0 ? (
              <MDTypography variant="caption" color="text">
                Nenhuma categoria cadastrada.
              </MDTypography>
            ) : (
              <List component="nav" disablePadding>
                {categoriasArvore.map((n) => (
                  <CategoriaTreeItem
                    key={n.id}
                    categoria={n}
                    level={0}
                    expanded={expanded}
                    onToggle={toggleExpanded}
                    vinculadas={categoriasVinculadas}
                    onToggleCategoria={onToggleCategoria}
                  />
                ))}
              </List>
            )}
          </MDBox>
        </MDBox>
      )}

      {opcaoVinculo === "global" && (
        <MDTypography variant="body2" color="text">
          Cupom válido para todos os produtos (sem vínculo).
        </MDTypography>
      )}
    </MDBox>
  );
}
